// A production in the menu: its episode (a series only) and its features; the tools of the feature in view as tabs.
import "./features.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { factsOf } from "../data/production.ts";
import { useSources } from "../data/sources.ts";
import { deriveSteps } from "../data/steps.ts";
import { type Department, isExtra } from "../departments.ts";
import { episodeName, filmEpisode } from "../items.ts";
import { atLeast } from "../lib.ts";
import { formatRoute, type Route } from "../route.ts";
import {
  episodeOfRoute,
  FEATURES,
  type FeatureId,
  featureEpisodeLoc,
  featureLoc,
  featureTabs,
  placeOf,
} from "../studio.ts";
import type { ElementSubtype } from "../types.ts";
import type { ProjectData } from "../useProject.ts";
import { useApp } from "./app.ts";
import { Icon, type IconName } from "./icons.tsx";

type ProjectRoute = Extract<Route, { view: "project" | "shot" | "element" }>;

const FEATURE_ICON: Record<FeatureId, IconName> = {
  home: "home",
  write: "write",
  breakdown: "breakdown",
  visualize: "visualize",
  create: "create",
  settings: "settings",
};

const storeKey = (projectId: string) => `fs.episode.${projectId}`;
const EVENT = "fs-episode";

/** The episode you last worked on in this production; it is the scope of every feature. */
export function useEpisodePick(projectId: string): [string | null, (id: string) => void] {
  const read = useCallback(() => {
    try {
      return localStorage.getItem(storeKey(projectId));
    } catch {
      return null;
    }
  }, [projectId]);
  const [picked, setPicked] = useState(read);
  useEffect(() => {
    const on = () => setPicked(read());
    on();
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, [read]);
  const pick = useCallback(
    (id: string) => {
      try {
        localStorage.setItem(storeKey(projectId), id);
      } catch {
        // private window: the choice lasts as long as the address says it
      }
      setPicked(id);
      window.dispatchEvent(new Event(EVENT));
    },
    [projectId],
  );
  return [picked, pick];
}

/** The episode in scope: the one in the address, else the last you worked on, else the newest. */
export function useCurrentEpisode(d: ProjectData | null, route: ProjectRoute): string | null {
  const [picked, pick] = useEpisodePick(route.projectId);
  const shotEpisode = route.view === "shot" ? d?.shotHome.get(route.shotId)?.episodeId : null;
  const fromRoute = episodeOfRoute(route, shotEpisode);
  useEffect(() => {
    if (fromRoute && fromRoute !== picked) pick(fromRoute);
  }, [fromRoute, picked, pick]);
  if (fromRoute) return fromRoute;
  if (!d) return picked;
  const live = d.episodes;
  return live.find((e) => e.id === picked)?.id ?? live.at(-1)?.id ?? null;
}

/** The extra departments an episode runs, for its tabs. */
function useExtras(d: ProjectData | null, episodeId: string | null): Department[] {
  const sources = useSources();
  const [extras, setExtras] = useState<Department[]>([]);
  const projectId = d?.project.id;
  // biome-ignore lint/correctness/useExhaustiveDependencies: read once per episode; a new shot does not change them
  useEffect(() => {
    if (!d || !episodeId) return setExtras([]);
    let live = true;
    sources.steps.episode(d.project.id, episodeId, factsOf(d, episodeId), d.project.name).then(
      (s) => {
        if (live)
          setExtras(
            deriveSteps(factsOf(d, episodeId), s?.signoffs)
              .map((x) => x.dept)
              .filter(isExtra),
          );
      },
      () => {},
    );
    return () => {
      live = false;
    };
  }, [sources, projectId, episodeId]);
  return extras;
}

/** The production's place in the menu: its episode (a series) and its six features. */
export function FeatureNav({ d, route }: { d: ProjectData; route: ProjectRoute }) {
  const { go } = useApp();
  const episodeId = useCurrentEpisode(d, route);
  const [, pick] = useEpisodePick(route.projectId);
  const subtype = useElementSubtype(d, route);
  const place = placeOf(route, subtype);
  const series = filmEpisode(d) === undefined && d.episodes.length > 0;
  const to = (feature: FeatureId) => ({
    view: "project" as const,
    projectId: route.projectId,
    loc: featureLoc(feature, episodeId),
  });
  return (
    <div className="fn">
      {series && (
        <label className="fn-ep">
          <span className="sr-only">Episode</span>
          <select
            value={episodeId ?? ""}
            onChange={(e) => {
              const id = e.target.value;
              pick(id);
              go({
                view: "project",
                projectId: route.projectId,
                loc: featureEpisodeLoc(route, id, subtype),
              });
            }}
          >
            {d.episodes.map((e) => (
              <option key={e.id} value={e.id}>
                {episodeName(e)}
              </option>
            ))}
          </select>
          <Icon name="chevron" size={12} className="down" />
        </label>
      )}
      <ul className="fn-list">
        {FEATURES.map((f) => (
          <li key={f.id}>
            <a
              className="side-link"
              href={formatRoute(to(f.id))}
              aria-current={place.feature === f.id ? "page" : undefined}
            >
              <Icon name={FEATURE_ICON[f.id]} size={15} />
              {f.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function useElementSubtype(d: ProjectData | null, route: ProjectRoute): ElementSubtype | undefined {
  return useMemo(
    () =>
      route.view === "element"
        ? (d?.elements.find((e) => e.id === route.elementId)?.subtype as ElementSubtype | undefined)
        : undefined,
    [d, route],
  );
}

/** The tools of the feature in view, on top. */
export function FeatureTabs({ d, route }: { d: ProjectData | null; route: ProjectRoute }) {
  const { role } = useApp();
  const episodeId = useCurrentEpisode(d, route);
  const extras = useExtras(d, episodeId);
  const place = placeOf(route, useElementSubtype(d, route));
  const tabs = featureTabs(place.feature, episodeId, { extras, makers: atLeast(role, "maker") });
  return (
    <nav className="ft" aria-label={FEATURES.find((f) => f.id === place.feature)?.label}>
      {tabs.map((t) => (
        <a
          key={t.id}
          className="ft-tab"
          href={formatRoute({ view: "project", projectId: route.projectId, loc: t.loc })}
          aria-current={t.id === place.tab ? "page" : undefined}
        >
          {t.label}
        </a>
      ))}
    </nav>
  );
}

/** Whether the menu shows the folders: only in Home's Files. */
export const showsFiles = (route: ProjectRoute): boolean =>
  route.view === "project" && (route.loc.kind === "assets" || route.loc.kind === "folder");
